import { ReactElement } from 'react';
import { v4 as uuid } from 'uuid';
import { AxiosError, AxiosResponse } from 'axios';
import i18n from 'i18next';

// project imports
import store from 'features';
import {
  getTranslationStringFromPath,
  isEmptyObject,
  isValidJson
} from 'utils';
import { openSnackbar } from 'features/app/appSlice';

// material-ui
import { Button } from '@mui/material';

// types
import { HttpHeaders, AnyParams, ErrorDetail } from 'models';
import { EtagEntityList } from 'models/etag';

// constants
export enum STATUS_CODE {
  ERROR_BAD_REQUEST = 400,
  ERROR_UNAUTHORIZED = 401,
  ERROR_FORBIDDEN = 403,
  ERROR_NOT_FOUND = 404,
  ERROR_PRECONDITION_FAILED = 412
}

// ==============================|| REST UTILS ||============================== //

export const createGetHeaderData = (
  memberAccountId = true,
  accept?: string
): HttpHeaders => {
  return createCommonHeaderData(memberAccountId, accept);
};

export const createDeleteHeaderData = (
  memberAccountId = true,
  accept?: string,
  ifMatch?: string,
  ifNonMatch?: string
): HttpHeaders => {
  const headers: HttpHeaders = {
    ...createCommonHeaderData(memberAccountId, accept)
  };

  if (ifMatch) headers['If-Match'] = ifMatch;
  if (ifNonMatch) headers['If-None-Match'] = ifNonMatch;

  return headers;
};

export const createHeaderData = (
  memberAccountId = true,
  accept?: string,
  ifMatch?: string,
  ifNonMatch?: string
): HttpHeaders => {
  const headers: HttpHeaders = {
    ...createCommonHeaderData(memberAccountId, accept),
    'Content-type': 'application/json'
  };

  if (ifMatch) headers['If-Match'] = ifMatch;
  if (ifNonMatch) headers['If-None-Match'] = ifNonMatch;

  return headers;
};

export const createPutHeaderData = (
  memberAccountId = true,
  accept?: string,
  contentType?: string,
  ifMatch?: string,
  ifNonMatch?: string
): HttpHeaders => {
  const headers: HttpHeaders = {
    ...createCommonHeaderData(memberAccountId, accept),
    'Content-Type': contentType || 'application/json'
  };

  if (ifMatch) headers['If-Match'] = ifMatch;
  if (ifNonMatch) headers['If-None-Match'] = ifNonMatch;

  return headers;
};

const createCommonHeaderData = (
  memberAccountId: boolean,
  accept?: string
): HttpHeaders => {
  const { account, tenant } = store.getState().account;

  const headers: HttpHeaders = {
    accept: accept || 'application/json',
    'Cache-Control': 'no-cache, no-store, must-revalidate',
    Pragma: 'no-cache',
    'Consuming-Component': 'aiems-dispatcher',
    'Session-Id': account?.accountInfo?.session_state || '',
    'Transaction-Id': uuid(),
    'Message-Id': uuid(),
    'Tenant-Id': tenant || '',
    Authorization: `Bearer ${account?.accessToken || ''}`,
    Username: account?.accountInfo?.preferred_username || '',
    'User-Id': account?.accountInfo?.sub || ''
  };

  if (memberAccountId)
    headers['Member-Account-Id'] = account?.accountInfo?.currentAccount || '';

  return headers;
};

export const createQueryParams = (params: AnyParams): string => {
  let queryParams = '';

  if (!isEmptyObject(params)) {
    Object.entries(params).forEach(([key, value]) => {
      if (
        !!value ||
        ['number', 'boolean', 'object'].indexOf(typeof value) > -1
      ) {
        queryParams += `${!queryParams ? '?' : '&'}${key}=${encodeURIComponent(
          value + ''
        )}`;
      }
    });
  }

  return queryParams;
};

export const handleAjaxLoader = (data: any, callback: () => void): void => {
  const isDataValidJson = isValidJson(data);

  (!isDataValidJson ||
    (isDataValidJson && JSON.parse(data)?.showAjaxLoader !== false)) &&
    callback();
};

export const handleAjaxEtag = (
  data: any,
  etagEntity: EtagEntityList,
  callback: () => void
): void => {
  const isDataValidJson = isValidJson(data);
  if (isDataValidJson && etagEntity) callback();
};

const getErrorMessageForResponseStatusCode = (
  statusCode: number | undefined
): string => {
  const i18nErrorStatus = 'common.httpErrorStatusMessages.';

  switch (statusCode) {
    case STATUS_CODE.ERROR_BAD_REQUEST:
      return i18n.t(i18nErrorStatus + 'error400BadRequest');
    case STATUS_CODE.ERROR_UNAUTHORIZED:
      return i18n.t(i18nErrorStatus + 'error401Unauthorized');
    case STATUS_CODE.ERROR_FORBIDDEN:
      return i18n.t(i18nErrorStatus + 'error403Forbidden');
    case STATUS_CODE.ERROR_NOT_FOUND:
      return i18n.t(i18nErrorStatus + 'error404NotFound');
    default:
      return i18n.t(i18nErrorStatus + 'error500InternalError');
  }
};

export const getErrorMessage = (response: AxiosResponse): string => {
  const errorDetail =
    !!response && isValidJson(response.data as string)
      ? (JSON.parse(response.data as string) as Partial<ErrorDetail>)
      : undefined;

  const errorDetailCode = errorDetail?.detail;
  const foundTranslation = getTranslationStringFromPath(
    `common.messages.caReasons.${errorDetailCode}`
  );
  const isPathValidTranslation = foundTranslation != null;
  if (isPathValidTranslation) {
    return foundTranslation;
  }
  const responseStatusMessage = getErrorMessageForResponseStatusCode(
    errorDetail?.status
  );
  const isCACode = errorDetailCode?.startsWith('CA_');
  if (!isCACode && errorDetailCode?.length) {
    return `${responseStatusMessage}: ${errorDetailCode}`;
  }
  return responseStatusMessage;
};

const callCallBack = (callBack?: () => void) => {
  if (callBack) {
    callBack();
  }
};

export const handlePreconditionFailed = (
  error: AxiosError,
  onReloadData: () => void,
  onForceSave?: () => void,
  callBack?: () => void
): void => {
  const handleReloadData = () => {
    onReloadData();
    store.dispatch(openSnackbar({}));
  };

  const handleForceSave = () => {
    onForceSave?.();
    store.dispatch(openSnackbar({}));
  };

  if (error.response?.status === STATUS_CODE.ERROR_PRECONDITION_FAILED) {
    const i18nMsgBase = 'common.messages.';
    const snackbarMessage: ReactElement = (
      <>
        {i18n.t(i18nMsgBase + 'ifNotMatch')}
        <br />
        {i18n.t(i18nMsgBase + 'ifNotMatchThen')}
        <Button variant='text' onClick={handleReloadData}>
          {i18n.t(i18nMsgBase + 'discardChanges')}
        </Button>
        {onForceSave && (
          <>
            {i18n.t(i18nMsgBase + 'or')}
            <Button variant='text' onClick={handleForceSave}>
              {i18n.t(i18nMsgBase + 'forceSave')}
            </Button>
          </>
        )}
        ? {/* question mark to be shown */}
      </>
    );

    store.dispatch(
      openSnackbar({
        open: true,
        message: snackbarMessage,
        close: true,
        actionButton: false,
        autoHideDuration: null
      })
    );
  } else {
    callCallBack(callBack);
  }
};

export const MAX_RETRIES = 3;
const MAX_RETRY_DELAY = 60000;

export const getReconnectionDelay = (retryCount: number): number => {
  return Math.min(1000 * 2 ** retryCount, MAX_RETRY_DELAY);
};
